import { Link, Navigate, useLocation } from "react-router-dom";
import Reveal from "@/components/Reveal";
import WordReveal from "@/components/WordReveal";
import { useSeo, SITE_URL } from "@/lib/seo";
import { breadcrumb } from "@/lib/structuredData";
import { useStartProject } from "@/lib/startProjectContext";
import { INTERNATIONAL_CITIES } from "@/data/internationalCities";
import { stripLocale } from "@/lib/i18n";

/**
 * Shared template for city-level international sourcing pages
 * (Phase 1: London, Manchester, Berlin, Munich, Paris, Lyon,
 * Amsterdam, Rotterdam). Data-driven from internationalCities.js.
 */
export const RegionCity = () => {
  const location = useLocation();
  const page = INTERNATIONAL_CITIES.find((p) => p.slug === stripLocale(location.pathname));
  const { open } = useStartProject();

  useSeo({
    title: page ? page.title : "International city programme",
    description: page ? page.metaDescription : undefined,
    path: page ? page.slug : "/regions",
    type: "article",
    jsonLd: page
      ? {
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Service",
              "@id": `${SITE_URL}${page.slug}#service`,
              name: page.h1,
              description: page.metaDescription,
              provider: { "@id": `${SITE_URL}#organization` },
              areaServed: {
                "@type": "City",
                name: page.city,
                containedInPlace: { "@type": "Country", name: page.parentCountryName },
              },
              serviceType: `India sourcing · buying agent · quality control · ${page.city}`,
              url: `${SITE_URL}${page.slug}`,
              isPartOf: {
                "@type": "WebPage",
                "@id": `${SITE_URL}${page.parentCountrySlug}`,
                name: `India Sourcing · ${page.parentCountryName}`,
              },
            },
            {
              "@type": "FAQPage",
              "@id": `${SITE_URL}${page.slug}#faq`,
              mainEntity: (page.faqs || []).map(([q, a]) => ({
                "@type": "Question",
                name: q,
                acceptedAnswer: { "@type": "Answer", text: a },
              })),
            },
            breadcrumb([
              { name: "Home", path: "/" },
              { name: "Regions", path: "/regions" },
              { name: page.parentCountryName, path: page.parentCountrySlug },
              { name: page.city, path: page.slug },
            ]),
          ],
        }
      : null,
    jsonLdId: "region-city-jsonld",
  });

  if (!page) return <Navigate to="/regions" replace />;

  const siblings = INTERNATIONAL_CITIES.filter(
    (p) => p.parentCountry === page.parentCountry && p.slug !== page.slug
  );

  return (
    <article data-testid="region-city-page">
      <header className="wrap" style={{ paddingBlock: "calc(74px + var(--section)) 2rem" }}>
        <Reveal as="nav" aria-label="Breadcrumbs" style={{ marginBottom: "1.2rem" }}>
          <p
            className="mono"
            style={{
              fontSize: ".72rem",
              color: "var(--brass-deep)",
              letterSpacing: ".14em",
              textTransform: "uppercase",
            }}
          >
            <Link to="/" style={{ color: "inherit" }} data-testid="region-city-crumb-home">
              Home
            </Link>{" "}
            ·{" "}
            <Link to="/regions" style={{ color: "inherit" }} data-testid="region-city-crumb-regions">
              Regions
            </Link>{" "}
            ·{" "}
            <Link
              to={page.parentCountrySlug}
              style={{ color: "inherit" }}
              data-testid="region-city-crumb-country"
            >
              {page.parentCountryName}
            </Link>{" "}
            · {page.city}
          </p>
        </Reveal>
        <Reveal>
          <p className="eyebrow" data-testid="region-city-eyebrow">
            {page.eyebrow}
          </p>
        </Reveal>
        <WordReveal
          as="h1"
          text={page.h1}
          className="display tp-title"
          stagger={70}
          testId="region-city-h1"
        />
        <Reveal
          as="p"
          delay={200}
          style={{
            maxWidth: "72ch",
            color: "var(--ink-soft)",
            fontSize: "1.1rem",
            marginTop: "1.4rem",
            lineHeight: 1.65,
          }}
          data-testid="region-city-lede"
        >
          {page.lede}
        </Reveal>
        <Reveal
          as="div"
          delay={280}
          style={{ display: "flex", flexWrap: "wrap", gap: ".8rem", marginTop: "2rem" }}
        >
          <button
            type="button"
            className="btn btn-fill btn-lg btn-glow"
            onClick={open}
            data-testid="region-city-cta-primary"
          >
            Start Your Project →
          </button>
          <Link
            to={page.parentCountrySlug}
            className="btn btn-ghost"
            data-testid="region-city-cta-country"
          >
            Back to {page.parentCountryName}
          </Link>
        </Reveal>
      </header>

      {/* KEY FACTS strip */}
      <section
        className="wrap"
        data-testid="region-city-facts"
        style={{
          paddingBlock: "clamp(2rem, 4vw, 3.4rem)",
          borderBlock: "1px solid var(--line)",
        }}
      >
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: "1.6rem 2rem",
          }}
        >
          {page.keyFacts.map(([k, v], i) => (
            <Reveal key={i}>
              <span
                className="mono"
                style={{
                  color: "var(--brass-deep)",
                  fontSize: ".68rem",
                  letterSpacing: ".14em",
                  textTransform: "uppercase",
                }}
              >
                {k}
              </span>
              <p
                style={{
                  fontFamily: "var(--display)",
                  fontWeight: 380,
                  fontSize: "1.05rem",
                  margin: ".3rem 0 0",
                  letterSpacing: "-.005em",
                  lineHeight: 1.35,
                }}
              >
                {v}
              </p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* BODY */}
      <section className="wrap" style={{ paddingBlock: "var(--section)", maxWidth: "820px" }}>
        {(page.sections || []).map((s, i) => (
          <Reveal
            key={i}
            as="section"
            style={{ marginTop: i === 0 ? 0 : "2.6rem" }}
            data-testid={`region-city-section-${i}`}
          >
            <h2
              className="display"
              style={{
                fontSize: "clamp(1.5rem, 2.4vw, 2rem)",
                margin: "0 0 1.1rem",
                letterSpacing: "-.01em",
                lineHeight: 1.15,
              }}
            >
              {s.h2}
            </h2>
            {(s.body || []).map((para, ix) => (
              <p
                key={ix}
                style={{
                  color: "var(--ink-soft)",
                  fontSize: "1.04rem",
                  lineHeight: 1.75,
                  margin: "0 0 1.1rem",
                  maxWidth: "68ch",
                }}
              >
                {para}
              </p>
            ))}
          </Reveal>
        ))}

        {/* Related services */}
        <Reveal
          as="section"
          style={{ marginTop: "2.6rem" }}
          data-testid="region-city-related-services"
        >
          <h2
            className="display"
            style={{
              fontSize: "clamp(1.5rem, 2.4vw, 2rem)",
              margin: "0 0 1.1rem",
              letterSpacing: "-.01em",
              lineHeight: 1.15,
            }}
          >
            Related programmes for {page.city} buyers
          </h2>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
              gap: "1rem",
              marginTop: "1rem",
            }}
          >
            {page.relatedServices.map((r, i) => (
              <Link
                key={i}
                to={r.slug}
                data-testid={`region-city-related-${i}`}
                style={{
                  display: "block",
                  padding: "1.2rem 1.3rem",
                  border: "1px solid var(--line)",
                  color: "var(--ink)",
                  textDecoration: "none",
                  background: "var(--paper)",
                  transition: "border-color 200ms, transform 200ms",
                }}
              >
                <p
                  className="mono"
                  style={{
                    color: "var(--brass-deep)",
                    fontSize: ".62rem",
                    letterSpacing: ".14em",
                    textTransform: "uppercase",
                    margin: "0 0 .4rem",
                  }}
                >
                  Service
                </p>
                <p
                  style={{
                    fontFamily: "var(--display)",
                    fontSize: "1.05rem",
                    letterSpacing: "-.005em",
                    margin: 0,
                  }}
                >
                  {r.name} →
                </p>
              </Link>
            ))}
          </div>
        </Reveal>
      </section>

      {/* FAQ */}
      <section
        className="wrap"
        data-testid="region-city-faq"
        style={{ paddingBlock: "var(--section)", maxWidth: "820px" }}
      >
        <h2
          className="display"
          style={{
            fontSize: "clamp(1.5rem, 2.4vw, 2rem)",
            margin: "0 0 1.4rem",
            letterSpacing: "-.01em",
          }}
        >
          Frequently asked questions — {page.city} buyers
        </h2>
        {(page.faqs || []).map(([q, a], i) => (
          <details
            key={i}
            data-testid={`region-city-faq-${i}`}
            style={{
              borderTop: i === 0 ? "1px solid var(--line)" : "none",
              borderBottom: "1px solid var(--line)",
              padding: "1.2rem 0",
            }}
          >
            <summary
              style={{
                cursor: "pointer",
                fontFamily: "var(--display)",
                fontSize: "1.1rem",
                letterSpacing: "-.005em",
                listStyle: "none",
              }}
            >
              {q}
            </summary>
            <p
              style={{
                color: "var(--ink-soft)",
                fontSize: "1rem",
                lineHeight: 1.7,
                margin: ".8rem 0 0",
                maxWidth: "68ch",
              }}
            >
              {a}
            </p>
          </details>
        ))}
      </section>

      {/* Sibling city cross-links */}
      {siblings.length > 0 && (
        <section
          className="wrap"
          data-testid="region-city-siblings"
          style={{ paddingBlock: "var(--section)", borderTop: "1px solid var(--line)" }}
        >
          <p
            className="mono"
            style={{
              color: "var(--brass-deep)",
              fontSize: ".68rem",
              letterSpacing: ".14em",
              textTransform: "uppercase",
              margin: "0 0 .8rem",
            }}
          >
            Other {page.parentCountryName} programmes
          </p>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
              gap: "1rem",
            }}
          >
            {siblings.map((s, i) => (
              <Link
                key={i}
                to={s.slug}
                data-testid={`region-city-sibling-${i}`}
                style={{
                  display: "block",
                  padding: "1.1rem 1.2rem",
                  border: "1px solid var(--line)",
                  color: "var(--ink)",
                  textDecoration: "none",
                  background: "var(--paper)",
                }}
              >
                <p
                  className="mono"
                  style={{
                    color: "var(--brass-deep)",
                    fontSize: ".62rem",
                    letterSpacing: ".14em",
                    textTransform: "uppercase",
                    margin: "0 0 .3rem",
                  }}
                >
                  City programme
                </p>
                <p
                  style={{
                    fontFamily: "var(--display)",
                    fontSize: "1.05rem",
                    letterSpacing: "-.005em",
                    margin: 0,
                  }}
                >
                  India sourcing · {s.city} →
                </p>
              </Link>
            ))}
          </div>
        </section>
      )}
    </article>
  );
};

export default RegionCity;
